import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';

interface TermlyScriptWrapperProps {
  policyId: string;
  className?: string;
}

export function TermlyScriptWrapper({ policyId, className }: TermlyScriptWrapperProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let mounted = true;
    let retryTimeout: NodeJS.Timeout;
    let initTimeout: NodeJS.Timeout;
    let scriptElement: HTMLScriptElement | null = null;

    const loadTermly = () => {
      setError(null);

      // Remove any existing script
      const existingScript = document.querySelector('script[src*="termly"]');
      if (existingScript) {
        existingScript.remove();
      }

      // Create container for policy
      if (containerRef.current) {
        const policyContainer = document.createElement('div');
        policyContainer.setAttribute('name', 'termly-embed');
        policyContainer.setAttribute('data-id', policyId);
        policyContainer.setAttribute('data-type', 'iframe');
        policyContainer.classList.add('termly-embed');

        // Clear existing content and append new container
        containerRef.current.innerHTML = '';
        containerRef.current.appendChild(policyContainer);

        // Add dark mode styles
        const styleElement = document.createElement('style');
        styleElement.textContent = `
          .termly-embed {
            --termly-text-color: var(--text-color, #1F2937);
            --termly-background-color: var(--background-color, #FFFFFF);
            --termly-link-color: var(--link-color, #7851A9);
          }
          
          .dark .termly-embed {
            --text-color: #E4E4E7;
            --background-color: #1E1E1E;
            --link-color: #9B7ACA;
          }

          .termly-embed iframe {
            background-color: var(--termly-background-color) !important;
            color: var(--termly-text-color) !important;
          }

          .termly-embed a {
            color: var(--termly-link-color) !important;
          }
        `;
        document.head.appendChild(styleElement);
      }

      // Create new script element
      scriptElement = document.createElement('script');
      scriptElement.src = 'https://app.termly.io/embed-policy.min.js';
      scriptElement.async = true;
      scriptElement.defer = true;

      scriptElement.onload = () => {
        if (mounted) {
          setIsLoading(false);
        }
      };

      scriptElement.onerror = () => {
        if (mounted) {
          setError('Failed to load policy script');
          if (retryCount < 3) {
            retryTimeout = setTimeout(() => {
              setRetryCount(prev => prev + 1);
              loadTermly();
            }, Math.pow(2, retryCount) * 1000);
          }
        }
      };

      // Add script to head
      document.head.appendChild(scriptElement);

      // Set timeout for overall loading
      initTimeout = setTimeout(() => {
        if (mounted && isLoading) {
          setError('Policy load timeout');
        }
      }, 30000); // 30 second timeout
    };

    // Start loading process
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', loadTermly);
    } else {
      loadTermly();
    }

    // Cleanup function
    return () => {
      mounted = false;
      if (retryTimeout) clearTimeout(retryTimeout);
      if (initTimeout) clearTimeout(initTimeout);
      if (scriptElement) scriptElement.remove();
      document.removeEventListener('DOMContentLoaded', loadTermly);

      // Remove any iframes created by Termly
      const iframes = document.querySelectorAll('iframe[src*="termly"]');
      iframes.forEach(iframe => iframe.remove());

      // Remove style element
      const styleElement = document.querySelector('style[data-termly-styles]');
      if (styleElement) styleElement.remove();
    };
  }, [policyId, retryCount]);

  const handleRetry = () => {
    setRetryCount(0);
    setIsLoading(true);
    setError(null);
  };

  return (
    <div className="relative min-h-[800px]">
      <div 
        ref={containerRef}
        className={className}
      />

      {isLoading && !error && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-dark-elevated">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-royal-purple animate-spin mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">
              Loading policy{retryCount > 0 ? ` (Attempt ${retryCount + 1}/4)` : ''}...
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-dark-elevated">
          <div className="text-center p-4">
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <div className="space-x-4">
              <button
                onClick={handleRetry}
                className="px-4 py-2 bg-royal-purple text-white rounded-lg hover:bg-royal-purple/90 transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-gray-200 dark:bg-dark-paper text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-dark-elevated transition-colors"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}